import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import siteSetting from '../../config/env';
import { useSelector } from 'react-redux';
import { matchNaturalLanguage, filterActions, getRecentItems, addRecentItem } from './searchPatterns';
import useWindowSize from '../new-service/useWindowSize';
import useSettingsTabs from '../settings/useSettingsTabs';
import { searchSettings } from '../settings/settingsHubIndex';
import moment from 'moment';
import { AGENT_VISIBLE } from '../../config/featureFlags';
import { baileyOff } from '../../_utils/receptionMode';
import { getAccess, isSelfEmployedMe } from '../permissions/access';
import './UniversalSearch.css';

const MIN_QUERY = 2;

// Anything typed can go to Bailey (owners and self-employed stylists, where
// Bailey is switched on): the Bailey tab opens and starts on it
const canAskBailey = () => {
  if (!AGENT_VISIBLE || baileyOff()) return false;
  try { return !!(getAccess().owner || isSelfEmployedMe()); } catch (e) { return false; }
};
const DEBOUNCE_MS = 200;
const CACHE_TTL = 30000; // 30 seconds

// Line icons matching the rest of the app. Emoji read as inconsistent next to
// the app's stroked iconography, and render differently across platforms.
const ICON_PATHS = {
  calendar: <><rect x="3" y="4.5" width="18" height="16.5" rx="3" /><path d="M3 9.5H21" /><path d="M8 2.5V5.5" /><path d="M16 2.5V5.5" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12L15 14" /></>,
  leave: <><circle cx="12" cy="12" r="4" /><path d="M12 2V4M12 20V22M4.9 4.9L6.3 6.3M17.7 17.7L19.1 19.1M2 12H4M20 12H22M4.9 19.1L6.3 17.7M17.7 6.3L19.1 4.9" /></>,
  person: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></>,
  people: <><circle cx="9" cy="8" r="3.6" /><path d="M2.5 21c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" /><path d="M16 4.6a3.6 3.6 0 0 1 0 6.9" /><path d="M18 14.9c2.1.8 3.5 2.8 3.5 5.1" /></>,
  money: <><path d="M9 21h8" /><path d="M7 13h7" /><path d="M15.5 4.8A4.3 4.3 0 0 0 8 8v6.5c0 2.5-1 4.4-2.5 5.5" /><path d="M6 21h3" /></>,
  list: <><path d="M8 6.5h13M8 12h13M8 17.5h13" /><path d="M3.5 6.5h.01M3.5 12h.01M3.5 17.5h.01" /></>,
  megaphone: <><path d="M3 11v2a1 1 0 0 0 1 1h3l7 5V5L7 10H4a1 1 0 0 0-1 1z" /><path d="M18 8.5a5 5 0 0 1 0 7" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.9 19l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 13.6H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.9 8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H10a1.6 1.6 0 0 0 1-1.5V4a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V10a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" /></>,
  document: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></>,
  sparkle: <><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
};

// Quick-action keys (searchPatterns) mapped onto the icon set above
const ACTION_ICONS = {
  booking: 'calendar', event: 'clock', leave: 'leave', client: 'person',
  diary: 'calendar', clients: 'people', finance: 'money', team: 'people',
  catalogue: 'list', marketing: 'megaphone', settings: 'settings', forms: 'document',
};

const ResultIcon = ({ name }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {ICON_PATHS[name] || ICON_PATHS.plus}
  </svg>
);

/**
 * UniversalSearch - Cmd+K command palette.
 * Searches clients, bookings, actions, and understands natural language.
 */
function UniversalSearch({ history }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { width } = useWindowSize();
  const isMobile = width <= 767;
  // Settings pages and their sections ("no show fee", "opening hours",
  // "bank account") are searchable here too, filtered by the same role and
  // reception-mode rules as the settings tab strip.
  const user = useSelector(s => s.user);
  const { tabs: settingsTabs } = useSettingsTabs(user);
  // Search is contextual: from a settings page, settings results come first.
  const settingsFirst = open && window.location.pathname.startsWith('/settings');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const cacheRef = useRef({});

  // Global Cmd+K listener. The sales engine has its own shell (SideNav hides
  // there too) and this palette searches clients and bookings, which mean
  // nothing on that side; there it stays out of the way. Read the pathname
  // live rather than from props: this handler is registered once.
  const onOwnShell = () => window.location.pathname.startsWith('/sales-x7k9');
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (onOwnShell()) return;
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Navigating into the sales shell with the palette open closes it
  useEffect(() => {
    if (open && onOwnShell()) setOpen(false);
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setClients([]);
      setBookings([]);
      setSelectedIndex(0);
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.focus();
      });
    }
  }, [open]);

  // Search with debounce — single unified API call
  const doSearch = useCallback((q) => {
    if (q.length < MIN_QUERY) {
      setClients([]);
      setBookings([]);
      setLoading(false);
      return;
    }

    // Check cache
    const cached = cacheRef.current[q];
    if (cached && Date.now() - cached.time < CACHE_TTL) {
      setClients(cached.clients || []);
      setBookings(cached.bookings || []);
      setLoading(false);
      return;
    }

    // Cancel previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    axios.get(siteSetting.api.v2 + 'search', {
      params: { q, clientLimit: 5, bookingLimit: 3 },
      headers: { accessToken: localStorage.getItem('accessToken') },
      signal: controller.signal,
    })
    .then(res => {
      const data = res?.data?.responseData || res?.data?.data || res?.data || {};
      const c = data.clients?.results || data.clients || [];
      const b = data.bookings?.results || data.bookings || [];
      cacheRef.current[q] = { clients: c, bookings: b, time: Date.now() };
      setClients(c);
      setBookings(b);
      setLoading(false);
      setSelectedIndex(0);
    })
    .catch(err => {
      if (axios.isCancel(err)) return; // request was superseded
      setClients([]);
      setBookings([]);
      setLoading(false);
    });
  }, []);

  const handleQueryChange = useCallback((e) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), DEBOUNCE_MS);
  }, [doSearch]);

  // Build flat results list for keyboard navigation
  const allResults = useMemo(() => {
    const items = [];
    const nlp = matchNaturalLanguage(query);
    const actions = filterActions(query);
    const recent = !query ? getRecentItems().slice(0, 5) : [];

    if (nlp) {
      items.push({ type: 'nlp', ...nlp, _key: 'nlp-0' });
    }

    if (!query && recent.length > 0) {
      recent.filter(r => r.name).forEach((r, i) => items.push({ type: 'recent', ...r, _key: `recent-${i}` }));
    }

    clients.slice(0, 5).forEach((c, i) => {
      const name = c.name || '';
      if (!name) return;
      items.push({
        // Navigate by the client's USER id (what the list + panel key on) — NOT
        // the legacy clientUser subdocument _id, which won't load the panel.
        type: 'client', _key: `client-${i}`,
        id: c.userId || c._id, name, email: c.email || '', phone: c.phone || '',
      });
    });

    bookings.slice(0, 3).forEach((b, i) => {
      items.push({
        type: 'booking', _key: `booking-${i}`,
        id: b._id,
        clientName: b.clientName || '',
        service: b.service || '',
        date: b.date,
        amount: b.amount,
        status: b.status,
        staffName: b.staffName || '',
      });
    });

    const settingItems = query.length >= MIN_QUERY
      ? searchSettings(query, settingsTabs, settingsFirst ? 6 : 4).map((r, i) => ({
          type: 'setting', _key: `setting-${i}`, id: r.tab.id, path: r.tab.path, label: r.tab.label, section: r.section,
        }))
      : [];
    // Opened from a settings page: settings results lead (keyboard order
    // follows the render order below).
    if (settingsFirst) items.unshift(...settingItems);
    else items.push(...settingItems);

    actions.forEach((a, i) => items.push({ type: 'action', ...a, _key: `action-${i}` }));

    if (query.trim().length >= 4 && canAskBailey()) items.push({ type: 'bailey', _key: 'bailey-0', text: query.trim() });

    return items;
  }, [query, clients, bookings, settingsTabs, settingsFirst]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allResults[selectedIndex];
      if (item) executeResult(item);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [allResults, selectedIndex]);

  // Quick-action and NLP paths carry a literal ':salonId' token (the real
  // routes are salon-scoped, e.g. DiaryV2 only mounts at /diary/:salonId —
  // pushing the bare path 404s into the app shell). Resolved here, once, at
  // click time rather than baked into the static action list.
  const resolvePath = (path) => path.replace(':salonId', localStorage.getItem('salonId'));

  const executeResult = useCallback((item) => {
    setOpen(false);

    if (item.type === 'client' || (item.type === 'recent' && item.recentType === 'client')) {
      addRecentItem({ type: 'recent', recentType: 'client', id: item.id, name: item.name, email: item.email });
      // Deep-link straight to the client — /clients/:id opens the detail panel.
      history.push('/clients/' + item.id);
    } else if (item.type === 'booking' || (item.type === 'recent' && item.recentType === 'booking')) {
      addRecentItem({ type: 'recent', recentType: 'booking', id: item.id, name: item.clientName, date: item.date });
      // Navigate to diary and open the booking detail
      const salonId = localStorage.getItem('salonId');
      const bookingId = item.id;
      if (bookingId) {
        // Navigate to diary, then trigger booking panel open
        history.push('/diary/' + salonId);
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('beu-open-booking', { detail: { bookingId, date: item.date } }));
        }, 500);
      }
    } else if (item.type === 'setting') {
      // SettingsPageLayout scrolls to the section heading named in state.
      history.push(item.path, item.section ? { scrollToHeading: item.section } : undefined);
    } else if (item.type === 'action') {
      if (item.action === 'navigate') {
        history.push(resolvePath(item.path));
      }
    } else if (item.type === 'nlp') {
      if (item.action === 'navigate') {
        if (item.dateOffset) {
          const targetDate = moment().add(item.dateOffset, 'days').format('YYYY-MM-DD');
          history.push('/diary/' + localStorage.getItem('salonId') + '?date=' + targetDate);
        } else {
          history.push(resolvePath(item.path));
        }
      } else if (item.action === 'clientHistory' && item.extractedName) {
        // Search for the client and navigate
        doSearch(item.extractedName);
        setQuery(item.extractedName);
      } else if (item.action === 'bookFor' && item.extractedName) {
        doSearch(item.extractedName);
        setQuery(item.extractedName);
      }
    } else if (item.type === 'bailey') {
      history.push('/assistant', { ask: item.text });
    } else if (item.type === 'recent') {
      if (item.recentType === 'client') history.push('/clients/' + item.id);
      else if (item.path) history.push(item.path);
    }
  }, [history, doSearch]);

  if (!open) return null;

  // Group results for display
  const nlpResult = allResults.find(r => r.type === 'nlp');
  const recentResults = allResults.filter(r => r.type === 'recent');
  const clientResults = allResults.filter(r => r.type === 'client');
  const bookingResults = allResults.filter(r => r.type === 'booking');
  const settingResults = allResults.filter(r => r.type === 'setting');
  const actionResults = allResults.filter(r => r.type === 'action');
  const baileyResult = allResults.find(r => r.type === 'bailey');

  let flatIndex = 0;
  const getIndex = () => flatIndex++;

  // Settings pages and sections
  const settingsGroup = settingResults.length > 0 && (

            <div className="us-group">
              <div className="us-group-header">Settings</div>
              {settingResults.map(r => {
                const idx = getIndex();
                return (
                  <button
                    key={r._key}
                    className={`us-result-item ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                    onClick={() => executeResult(r)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="us-result-icon"><ResultIcon name="settings" /></span>
                    <span className="us-result-info">
                      <span className="us-result-name">{r.section || r.label}</span>
                      {r.section && <span className="us-result-meta">Settings · {r.label}</span>}
                    </span>
                    <span className="us-result-badge">Setting</span>
                  </button>
                );
              })}
            </div>
  );

  return ReactDOM.createPortal(
    <div className="us-overlay" onClick={() => setOpen(false)}>
      <div className="us-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        {/* Cmd+K hint bar */}
        <div className="us-hint-bar">
          <kbd className="us-hint-kbd">{navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+K</kbd>
          <span className="us-hint-text">to open and close</span>
          <button className="us-close-btn" onClick={() => setOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Search Input. On mobile the wrap becomes an iOS-style grey field
            with a Cancel button beside it, replacing the Cmd+K hint bar. */}
        <div className="us-search-row">
          <div className="us-input-wrap">
            <svg className="us-input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              className="us-input"
              type="text"
              placeholder={isMobile ? 'Search clients, bookings' : 'Search clients, bookings, actions...'}
              value={query}
              onChange={handleQueryChange}
              autoComplete="off"
              spellCheck="false"
            />
            {query && (
              <button className="us-input-clear" onClick={() => { setQuery(''); setClients([]); setBookings([]); if (inputRef.current) inputRef.current.focus(); }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M4 4L10 10M10 4L4 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
          <button className="us-cancel-btn" type="button" onClick={() => setOpen(false)}>Cancel</button>
        </div>

        {/* Results */}
        <div className="us-results">
          {loading && query.length >= MIN_QUERY && (
            <div className="us-loading">
              <div className="us-loading-dot" /><div className="us-loading-dot" /><div className="us-loading-dot" />
            </div>
          )}

          {settingsFirst && settingsGroup}

          {/* Natural language result */}
          {nlpResult && (() => {
            const idx = getIndex();
            return (
              <div className="us-group">
                <button
                  className={`us-result-item us-result-item--nlp ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                  onClick={() => executeResult(nlpResult)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="us-result-icon us-result-icon--nlp"><ResultIcon name="sparkle" /></span>
                  <span className="us-result-info">
                    <span className="us-result-name">{nlpResult.label}</span>
                  </span>
                  <span className="us-result-badge">Smart</span>
                </button>
              </div>
            );
          })()}

          {/* Recent items */}
          {recentResults.length > 0 && (
            <div className="us-group">
              <div className="us-group-header">Recent</div>
              {recentResults.map(r => {
                const idx = getIndex();
                return (
                  <button
                    key={r._key}
                    className={`us-result-item ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                    onClick={() => executeResult(r)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="us-result-icon"><ResultIcon name={r.recentType === 'client' ? 'person' : 'calendar'} /></span>
                    <span className="us-result-info">
                      <span className="us-result-name">{r.name}</span>
                      {r.email && <span className="us-result-meta">{r.email}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Clients */}
          {clientResults.length > 0 && (
            <div className="us-group">
              <div className="us-group-header">Clients</div>
              {clientResults.map(c => {
                const idx = getIndex();
                // Placeholder @beuapp.com addresses aren't shown, so build the
                // meta line from what actually renders — otherwise a hidden
                // email still contributes its " · " separator.
                const shownEmail = c.email && !c.email.includes('@beuapp.com') ? c.email : '';
                const meta = [shownEmail, c.phone].filter(Boolean).join(' · ');
                return (
                  <button
                    key={c._key}
                    className={`us-result-item ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                    onClick={() => executeResult(c)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="us-result-avatar">{(c.name || '?')[0]}</span>
                    <span className="us-result-info">
                      <span className="us-result-name">{c.name}</span>
                      <span className="us-result-meta">{meta}</span>
                    </span>
                    <span className="us-result-badge">Client</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Bookings */}
          {bookingResults.length > 0 && (
            <div className="us-group">
              <div className="us-group-header">Bookings</div>
              {bookingResults.map(b => {
                const idx = getIndex();
                return (
                  <button
                    key={b._key}
                    className={`us-result-item ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                    onClick={() => executeResult(b)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="us-result-icon"><ResultIcon name="calendar" /></span>
                    <span className="us-result-info">
                      <span className="us-result-name">{b.clientName || 'Booking'}{b.service ? ` · ${b.service}` : ''}</span>
                      <span className="us-result-meta">
                        {[
                          b.date ? moment(b.date).format('ddd D MMM') : '',
                          b.staffName || '',
                          b.amount ? `£${parseFloat(b.amount).toFixed(2)}` : '',
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="us-result-badge">Booking</span>
                  </button>
                );
              })}
            </div>
          )}

          {!settingsFirst && settingsGroup}

          {/* Actions */}
          {actionResults.length > 0 && (
            <div className="us-group">
              {query && <div className="us-group-header">Actions</div>}
              {!query && <div className="us-group-header">Quick Actions</div>}
              {actionResults.map(a => {
                const idx = getIndex();
                return (
                  <button
                    key={a._key}
                    className={`us-result-item ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                    onClick={() => executeResult(a)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="us-result-icon"><ResultIcon name={ACTION_ICONS[a.icon] || 'plus'} /></span>
                    <span className="us-result-info">
                      <span className="us-result-name">{a.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!loading && query.length >= MIN_QUERY && clientResults.length === 0 && bookingResults.length === 0 && settingResults.length === 0 && !nlpResult && (
            <div className="us-empty">No results for "{query}"</div>
          )}

          {/* Or ask Bailey to do it (last, as in the keyboard order) */}
          {baileyResult && (() => {
            const idx = getIndex();
            return (
              <div className="us-group">
                <div className="us-group-header">Bailey</div>
                <button
                  className={`us-result-item us-result-item--nlp ${selectedIndex === idx ? 'us-result-item--selected' : ''}`}
                  onClick={() => executeResult(baileyResult)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="us-result-icon us-result-icon--nlp"><ResultIcon name="sparkle" /></span>
                  <span className="us-result-info">
                    <span className="us-result-name">Ask Bailey: “{baileyResult.text}”</span>
                    <span className="us-result-meta">Bailey does it and shows you before anything happens</span>
                  </span>
                  <span className="us-result-badge">Bailey</span>
                </button>
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="us-footer">
          <span className="us-footer-hint"><kbd>↑↓</kbd> navigate</span>
          <span className="us-footer-hint"><kbd>⏎</kbd> select</span>
          <span className="us-footer-hint"><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default withRouter(UniversalSearch);
